import { Controller } from "@hotwired/stimulus"

// Assign pop-up on the CPFL Responsibilities card (Aline, 2026-10-29): pick a
// team member and a responsibility, save through the same endpoint as a team
// row's "Edit Responsibility", then swap the card and that member's team
// badge in place — no page reload.
export default class extends Controller {
  static targets = ["personSelect", "roleSelect"]
  static values = {
    url: String,
    csrfToken: String,
    savedText: String,
    failedText: String
  }

  // Runs each time the pop-up opens. The card carries the current assignments,
  // so a change made from a team row shows here too.
  prepare() {
    const assignments = this._assignments()
    const labelNames = {}
    Array.from(this.roleSelectTarget.options).forEach(o => { labelNames[o.value] = o.textContent.trim() })

    Array.from(this.personSelectTarget.options).forEach(o => {
      const labelId = assignments[o.value] ? String(assignments[o.value]) : ""
      o.dataset.labelId = labelId
      o.textContent = labelId && labelNames[labelId] ? `${o.dataset.name} — ${labelNames[labelId]}` : o.dataset.name
    })
    this.syncRole()
  }

  syncRole() {
    const option = this.personSelectTarget.selectedOptions[0]
    this.roleSelectTarget.value = option?.dataset.labelId || "n/a"
  }

  save(event) {
    event.preventDefault()
    const userId = this.personSelectTarget.value
    if (!userId) return

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: userId, label_id: this.roleSelectTarget.value, source: "responsibilities_card" })
    })
      .then(response => (response.ok ? response.json() : Promise.reject(response)))
      .then(data => {
        const card = document.getElementById("responsibilities-card")
        if (card && data.card_html) card.outerHTML = data.card_html
        this._updateTeamBadge(userId, data.label)
        this._closeModal()
        this._showToast(this.savedTextValue, "notice")
      })
      .catch(() => this._showToast(this.failedTextValue, "alert"))
  }

  _assignments() {
    try {
      return JSON.parse(document.getElementById("responsibilities-card")?.dataset.assignments || "{}")
    } catch {
      return {}
    }
  }

  // Same badge markup as team_member_controller#saveLabel and _team_member_row.
  _updateTeamBadge(userId, label) {
    const html = label && label !== "Group Admin"
      ? `<span class="shrink-0 rounded-full border border-blue-300 text-blue-400 bg-white" style="font-size: 14px; padding: 1px 8px;">${this._escapeHtml(label)}</span>`
      : ""
    document.querySelectorAll(`[data-team-member-user-id-value="${userId}"] [data-team-member-target~="labelBadge"]`)
      .forEach(badge => { badge.innerHTML = html })
  }

  _closeModal() {
    const modal = this.element.querySelector('[data-modal-id-value="assign-responsibility"]')
    const modalController = modal && this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (modalController) modalController.close()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _showToast(message, type = "notice") {
    const cssType = type === "alert" ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
